<template>
  <div class="width-100p">
    <v-btn
      depressed
      large
      block
      color="white"
      class="ma-0"
      @click="dialog = true"
    >
      Transfer testators
    </v-btn>

    <v-dialog
      v-model="dialog"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <v-card class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 class="text-h5">Transfer testators</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="closeDialog()"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <div v-if="loading" class="text-center">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <p v-else-if="!managedTestators.length">
            This advisor does not have any testators to transfer
          </p>
          <div v-else>
            <p>
              Which advisor do you want to transfer {{ advisorName }}'s
              testators to?
            </p>
            <ValidationObserver ref="observer">
              <ValidationProvider
                id="transfer_to_advisor"
                v-slot="{ errors }"
                name="transfer_to_advisor"
                rules="required"
              >
                <v-autocomplete
                  v-model="transferToAdvisorId"
                  :items="advisorList"
                  label="Advisor to transfer testators to*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <v-checkbox
                v-model="allCheckbox"
                label="Select all"
                @change="allCheckboxChanged"
              />

              <v-checkbox
                v-for="testator in managedTestators"
                :key="testator.id"
                v-model="testatorIds"
                :label="testator.user.fullName || testator.user.email"
                :value="testator.id"
                class="managed-testator-checkbox"
                dense
                @change="testatorSelected"
              />

              <ValidationProvider
                id="selected_testators"
                v-slot="{ errors }"
                name="selected_testators"
                :rules="{ is: [true] }"
              >
                <input v-model="testatorsSelected" type="hidden" />
                <span class="error-text">{{ errors[0] }}</span>
              </ValidationProvider>

              <v-divider />

              <ValidationProvider
                id="are_you_sure"
                v-slot="{ errors }"
                name="are_you_sure"
                :rules="{ is: [true] }"
              >
                <v-checkbox
                  v-model="areYouSure"
                  color="primary"
                  :error-messages="errors"
                  label="Are you sure you want to transfer these testators to another advisor"
                />
              </ValidationProvider>
            </ValidationObserver>
          </div>
        </v-card-text>

        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col cols="12">
              <v-btn
                class="left-edit-btn"
                :loading="buttonLoading"
                rounded
                depressed
                color="primary"
                @click="submit()"
              >
                Transfer testators
              </v-btn>
              <v-btn
                class="right-edit-btn"
                rounded
                depressed
                outlined
                color="primary"
                @click="closeDialog()"
              >
                Close
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />
  </div>
</template>

<script>
  import {
    COMPANY_USERS_QUERY,
    TRANSFER_TESTATORS_MUTATION,
  } from '../../../constants/graphql/companies';
  import { ADVISOR_MANAGED_TESTATORS_QUERY } from '../../../constants/graphql/advisors';
  import ErrorSnackbar from '../../common/ErrorSnackbar.vue';
  import SuccessSnackbar from '../../common/SuccessSnackbar.vue';

  export default {
    name: 'TransferTestators',
    components: {
      ErrorSnackbar,
      SuccessSnackbar,
    },
    props: {
      advisorId: {
        type: String,
        required: true,
      },
      companyId: {
        type: String,
        required: true,
      },
      advisorName: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        dialog: false,
        showError: false,
        showSuccess: false,
        transferToAdvisorId: null,
        testatorIds: [],
        buttonLoading: false,
        companyUsers: [],
        errorMessage: null,
        successMessage: null,
        areYouSure: false,
        managedTestators: [],
        allCheckbox: false,
        loading: true,
      };
    },
    computed: {
      advisorList() {
        return this.companyUsers.map(user => ({
          text: user.email,
          value: user.id,
          disabled: user.id == this.advisorId,
        }));
      },
      testatorsSelected() {
        return this.testatorIds.length > 0;
      },
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.transferTestators();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      transferTestators() {
        const advisorId = this.advisorId;
        const transferToAdvisorId = this.transferToAdvisorId;
        const testatorIds = this.testatorIds;

        this.$apollo
          .mutate({
            mutation: TRANSFER_TESTATORS_MUTATION,
            variables: {
              advisorId,
              transferToAdvisorId,
              testatorIds,
            },
          })
          .then(response => {
            this.successMessage = response.data.transferTestators;
            this.closeDialog();
            this.showSuccess = true;
            this.$apollo.queries.managedTestators.refetch();
          })
          .catch(error => {
            this.errorMessage = error.message
              .replace('GraphQL error:', '')
              .trim();
            this.closeDialog();
            this.showError = true;
            throw error;
          });
      },
      closeDialog() {
        this.dialog = false;
        this.transferToAdvisorId = null;
        this.buttonLoading = false;
        this.testatorIds = [];
        this.allCheckbox = false;
        if (this.$refs.observer) {
          this.$refs.observer.reset();
        }
      },
      allCheckboxChanged() {
        if (this.allCheckbox) {
          this.testatorIds = this.managedTestators.map(t => {
            return t.id;
          });
        } else {
          this.testatorIds = [];
        }
      },
      testatorSelected() {
        if (this.testatorIds.length == this.managedTestators.length) {
          this.allCheckbox = true;
        } else {
          this.allCheckbox = false;
        }
      },
    },
    apollo: {
      companyUsers: {
        query: COMPANY_USERS_QUERY,
        variables() {
          return {
            id: this.companyId,
            includeTestators: false,
          };
        },
      },
      managedTestators: {
        query: ADVISOR_MANAGED_TESTATORS_QUERY,
        variables() {
          return {
            id: this.advisorId,
          };
        },
        update: data => data.user.managedTestators,
        fetchPolicy: 'cache-and-network',
        result(response) {
          if (response.data.user) {
            this.loading = false;
          }
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  .managed-testator-checkbox {
    margin-top: 0;
  }
</style>
